export interface ImageResizeOptions {
  width?: number
  height?: number
  quality?: number
  type?: 'image/jpeg' | 'image/png' | 'image/webp'
  mode?: 'contain' | 'cover' | 'fill'
}

export interface ImageCompressOptions {
  maxWidth?: number
  maxHeight?: number
  quality?: number
  type?: 'image/jpeg' | 'image/png' | 'image/webp'
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

export function imageToCanvas(img: HTMLImageElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0)
  return canvas
}

export function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string = 'image/png',
  quality: number = 0.92
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to convert canvas to blob'))
        }
      },
      type,
      quality
    )
  })
}

export function canvasToDataURL(
  canvas: HTMLCanvasElement,
  type: string = 'image/png',
  quality: number = 0.92
): string {
  return canvas.toDataURL(type, quality)
}

export async function resizeImage(
  file: File,
  options: ImageResizeOptions = {}
): Promise<Blob> {
  const { width, height, quality = 0.92, type = 'image/jpeg', mode = 'contain' } = options

  const img = await loadImage(URL.createObjectURL(file))
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!

  let targetWidth = width || img.width
  let targetHeight = height || img.height

  if (mode === 'contain') {
    const ratio = Math.min(targetWidth / img.width, targetHeight / img.height)
    targetWidth = img.width * ratio
    targetHeight = img.height * ratio
  } else if (mode === 'cover') {
    const ratio = Math.max(targetWidth / img.width, targetHeight / img.height)
    targetWidth = img.width * ratio
    targetHeight = img.height * ratio
  }

  canvas.width = targetWidth
  canvas.height = targetHeight
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight)

  URL.revokeObjectURL(img.src)

  return canvasToBlob(canvas, type, quality)
}

export async function compressImage(
  file: File,
  options: ImageCompressOptions = {}
): Promise<Blob> {
  const { maxWidth = 1920, maxHeight = 1080, quality = 0.8, type = 'image/jpeg' } = options

  const img = await loadImage(URL.createObjectURL(file))

  let width = img.width
  let height = img.height

  if (width > maxWidth) {
    height = (maxWidth / width) * height
    width = maxWidth
  }

  if (height > maxHeight) {
    width = (maxHeight / height) * width
    height = maxHeight
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0, width, height)

  URL.revokeObjectURL(img.src)

  return canvasToBlob(canvas, type, quality)
}

export async function cropImage(
  file: File,
  x: number,
  y: number,
  width: number,
  height: number
): Promise<Blob> {
  const img = await loadImage(URL.createObjectURL(file))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, x, y, width, height, 0, 0, width, height)

  URL.revokeObjectURL(img.src)

  return canvasToBlob(canvas)
}

export async function rotateImage(file: File, degrees: number): Promise<Blob> {
  const img = await loadImage(URL.createObjectURL(file))
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!

  const radians = (degrees * Math.PI) / 180
  const sin = Math.abs(Math.sin(radians))
  const cos = Math.abs(Math.cos(radians))
  const newWidth = img.width * cos + img.height * sin
  const newHeight = img.width * sin + img.height * cos

  canvas.width = newWidth
  canvas.height = newHeight
  ctx.translate(newWidth / 2, newHeight / 2)
  ctx.rotate(radians)
  ctx.drawImage(img, -img.width / 2, -img.height / 2)

  URL.revokeObjectURL(img.src)

  return canvasToBlob(canvas)
}

export async function flipImage(file: File, horizontal: boolean = true): Promise<Blob> {
  const img = await loadImage(URL.createObjectURL(file))
  const canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height
  const ctx = canvas.getContext('2d')!

  if (horizontal) {
    ctx.translate(img.width, 0)
    ctx.scale(-1, 1)
  } else {
    ctx.translate(0, img.height)
    ctx.scale(1, -1)
  }

  ctx.drawImage(img, 0, 0)

  URL.revokeObjectURL(img.src)

  return canvasToBlob(canvas)
}

export async function imageToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export function base64ToImage(base64: string): HTMLImageElement {
  const img = new Image()
  img.src = base64
  return img
}

export function getImageInfo(file: File): Promise<{
  width: number
  height: number
  type: string
  size: number
}> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(img.src)
      resolve({
        width: img.width,
        height: img.height,
        type: file.type,
        size: file.size,
      })
    }
    img.onerror = reject
    img.src = URL.createObjectURL(file)
  })
}

export function isImage(file: File): boolean {
  return file.type.startsWith('image/')
}

export function isValidImageType(type: string): boolean {
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
  return validTypes.includes(type)
}

export function getImageMIME(extension: string): string {
  const mimeTypes: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    bmp: 'image/bmp',
    tiff: 'image/tiff',
  }
  return mimeTypes[extension.toLowerCase()] || 'image/png'
}
